// src/pages/AllLegionsTimeline.jsx
import React, { useState, useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";

import legions from "../data/Legions.json";
import periods from "../data/periods.json";
import events from "../data/LegionsTimeline.json";


// Leaflet default icon fix
const defaultIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -34],
  shadowSize: [41, 41],
});

const AllLegionsTimeline = () => {
  const [activePeriodId, setActivePeriodId] = useState(periods[0]?.id || "");
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedLegionIds, setSelectedLegionIds] = useState(
    legions.map((l) => l.id) // all selected initially
  );

  const activePeriod = useMemo(
    () => periods.find((p) => p.id === activePeriodId) || periods[0],
    [activePeriodId]
  );

  // Events for this period + filter
  const periodEvents = useMemo(
    () =>
      events.filter(
        (e) =>
          e.periodId === activePeriod.id &&
          selectedLegionIds.includes(e.legionId)
      ),
    [activePeriod.id, selectedLegionIds]
  );

  // Legions that actually appear in this period (for legend)
  const activeLegions = useMemo(() => {
    const ids = new Set(
      events
        .filter((e) => e.periodId === activePeriod.id)
        .map((e) => e.legionId)
    );
    return legions.filter((l) => ids.has(l.id));
  }, [activePeriod.id]);

  // Autoplay: cycle through periods every 7 seconds
  useEffect(() => {
    if (!isPlaying) return;

    const timer = setInterval(() => {
      setActivePeriodId((prevId) => {
        const idx = periods.findIndex((p) => p.id === prevId);
        if (idx === -1) return periods[0].id;
        const nextIdx = (idx + 1) % periods.length;
        return periods[nextIdx].id;
      });
    }, 7000);

    return () => clearInterval(timer);
  }, [isPlaying]);

  // Map centre: average of event positions, or fallback
  const mapCenter = useMemo(() => {
    if (periodEvents.length === 0) {
      return [54, -2.5]; // Britain-ish default
    }
    const avgLat =
      periodEvents.reduce((sum, e) => sum + e.lat, 0) / periodEvents.length;
    const avgLng =
      periodEvents.reduce((sum, e) => sum + e.lng, 0) / periodEvents.length;
    return [avgLat, avgLng];
  }, [periodEvents]);

  // Helpers
  const allLegionIds = legions.map((l) => l.id);
  const showingAll = selectedLegionIds.length === allLegionIds.length;

  const toggleLegion = (id) => {
    setSelectedLegionIds((prev) => {
      if (prev.includes(id)) {
        const next = prev.filter((x) => x !== id);
        return next.length === 0 ? allLegionIds : next; // never allow empty
      } else {
        return [...prev, id];
      }
    });
  };

  const showAllLegions = () => setSelectedLegionIds(allLegionIds);

  const getLegion = (id) => legions.find((l) => l.id === id);

  return (
    <div className="legions-wrapper">
      <h1 className="legions-title">Legions of Britannia – Strategic Timeline</h1>

      {/* Legion filter bar */}
      <div className="legions-filter-bar">
        <button
          className={
            showingAll
              ? "legions-filter-all active"
              : "legions-filter-all"
          }
          onClick={showAllLegions}
        >
          All legions
        </button>

        <div className="legions-filter-legions">
          {legions.map((legion) => {
            const isActive = selectedLegionIds.includes(legion.id);
            return (
              <button
                key={legion.id}
                className={
                  isActive
                    ? "legions-filter-chip active"
                    : "legions-filter-chip"
                }
                onClick={() => toggleLegion(legion.id)}
              >
                <span
                  className="legions-filter-dot"
                  style={{ backgroundColor: legion.color }}
                />
                <span className="legions-filter-label">{legion.code}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="legions-layout">
        {/* LEFT PANE: Text */}
        <div className="legions-left">
          <div className="legions-period-header">
            <h2>{activePeriod.label}</h2>
            <p className="legions-years">{activePeriod.years}</p>
          </div>

          <div className="legions-period-summary">
            {activePeriod.summary}
          </div>

          <div className="legions-legend">
            <h3>Legions active in this period</h3>
            {activeLegions.length === 0 && (
              <p className="legions-muted">
                No legions recorded in this dataset for this period yet.
              </p>
            )}
            <ul>
              {activeLegions.map((legion) => (
                <li key={legion.id}>
                  <span
                    className="legions-legend-dot"
                    style={{ backgroundColor: legion.color }}
                  />
                  <strong>{legion.name}</strong>{" "}
                  <span className="legions-legend-code">
                    ({legion.code}, emblem: {legion.emblem})
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="legions-sites">
            <h3>Sites & operations in this period</h3>
            {periodEvents.length === 0 && (
              <p className="legions-muted">
                No sites for the currently selected legion(s) in this period.
              </p>
            )}
            {periodEvents.map((evt) => {
              const legion = getLegion(evt.legionId);
              return (
                <div key={evt.id} className="legions-site-card">
                  <div className="legions-site-header">
                    <span
                      className="legions-site-dot"
                      style={{ backgroundColor: legion?.color || "#6b7280" }}
                    />
                    <div>
                      <strong>{evt.name}</strong>
                      <div className="legions-site-legion">
                        {legion ? legion.name : evt.legionId} – {evt.role}
                      </div>
                    </div>
                  </div>
                  <p className="legions-site-summary">{evt.summary}</p>
                </div>
            );
            })}
          </div>
        </div>

        {/* RIGHT PANE: Map */}
        <div className="legions-right">
          <MapContainer
            key={activePeriod.id + selectedLegionIds.join(",")}
            center={mapCenter}
            zoom={6}
            style={{ width: "100%", height: "100%" }}
            scrollWheelZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {periodEvents.map((evt) => {
              const legion = getLegion(evt.legionId);
              return (
                <Marker
                  key={evt.id}
                  position={[evt.lat, evt.lng]}
                  icon={defaultIcon}
                >
                  <Popup>
                    <strong>{evt.name}</strong>
                    <br />
                    <em>{legion ? legion.name : evt.legionId}</em>
                    <br />
                    <span>{evt.role}</span>
                    <br />
                    <br />
                    <span>{evt.summary}</span>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>
      </div>

      {/* TIMELINE CONTROLS */}
      <div className="legions-timeline-controls">
        <button
          className="legions-playpause"
          onClick={() => setIsPlaying((prev) => !prev)}
        >
          {isPlaying ? "Pause" : "Play"}
        </button>

        <div className="legions-period-buttons">
          {periods.map((period) => (
            <button
              key={period.id}
              className={
                period.id === activePeriod.id
                  ? "legions-period-button active"
                  : "legions-period-button"
              }
              onClick={() => {
                setActivePeriodId(period.id);
                setIsPlaying(false);
              }}
            >
              <span className="legions-period-label">{period.label}</span>
              <span className="legions-period-years">{period.years}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AllLegionsTimeline;
